import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FaArrowLeft, FaBriefcase, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import { supabase } from "../../supabaseClient";
import { formatProfileName } from "../../utils/profileName";
import ApplicationChat from "../../components/ApplicationChat";

const FIXED_QUESTIONS = [
  { key: "roblox_username", label: "Roblox username" },
  { key: "discord_username", label: "Discord username" },
  { key: "email", label: "Email" },
  { key: "roleplay_name", label: "Roleplay Name" },
];

function normaliseQuestions(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
}

function formatDate(value) {
  if (!value) return "Open until filled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Open until filled";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function PublicVacancy({ session, profile }) {
  const { id } = useParams();
  const location = useLocation();
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [application, setApplication] = useState(null);
  const [fixedAnswers, setFixedAnswers] = useState({
    roblox_username: profile?.roblox_name || "",
    discord_username: profile?.discord_name || "",
    email: profile?.email?.endsWith("@ruht.cloud") ? "" : (profile?.email || ""),
    roleplay_name: formatProfileName(profile, ""),
  });
  const [customAnswers, setCustomAnswers] = useState({});

  useEffect(() => {
    let active = true;
    supabase
      .from("public_vacancies")
      .select("*")
      .eq("id", id)
      .maybeSingle()
      .then(async ({ data, error: fetchError }) => {
        if (!active) return;
        if (data && !fetchError) {
          setVacancy(data);
          setLoading(false);
          return;
        }
        if (session?.user?.id) {
          const { data: applied } = await supabase.from("dc_applications").select("id").eq("vacancy_id", id).eq("user_id", session.user.id).limit(1).maybeSingle();
          if (applied) {
            const { data: privateVacancy } = await supabase.from("dc_vacancies").select("*").eq("id", id).maybeSingle();
            if (!active) return;
            if (privateVacancy) {
              setVacancy(privateVacancy);
              setLoading(false);
              return;
            }
          }
        }
        setError("This vacancy could not be found or is no longer available.");
        setLoading(false);
      });
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (!session?.user?.id || !id) { setApplication(null); return; }
    let active = true;
    supabase.from("dc_applications").select("*").eq("vacancy_id", id).eq("user_id", session.user.id).order("created_at", { ascending: false }).limit(1).maybeSingle().then(({ data }) => {
      if (!active) return;
      setApplication(data || null);
      if (data) setSubmitted(true);
    });
    return () => { active = false; };
  }, [id, session?.user?.id]);

  useEffect(() => {
    setFixedAnswers((current) => ({
      ...current,
      roblox_username: current.roblox_username || profile?.roblox_name || "",
      discord_username: current.discord_username || profile?.discord_name || "",
      email: current.email || (profile?.email?.endsWith("@ruht.cloud") ? "" : (profile?.email || "")),
      roleplay_name: current.roleplay_name || formatProfileName(profile, ""),
    }));
  }, [profile]);

  const questions = useMemo(() => normaliseQuestions(vacancy?.questions), [vacancy]);

  async function submitApplication(event) {
    event.preventDefault();
    if (!session?.user?.id) return;
    setSubmitting(true);
    setError("");

    const missingFixed = FIXED_QUESTIONS.find((question) => !String(fixedAnswers[question.key] || "").trim());
    if (missingFixed) {
      setError(`${missingFixed.label} is required.`);
      setSubmitting(false);
      return;
    }

    const missingCustom = questions.find((question) => question.required && !String(customAnswers[question.id] ?? "").trim());
    if (missingCustom) {
      setError(`${missingCustom.label || missingCustom.question || "A required question"} must be completed.`);
      setSubmitting(false);
      return;
    }

    const { data: existing } = await supabase
      .from("dc_applications")
      .select("id")
      .eq("vacancy_id", vacancy.id)
      .eq("user_id", session.user.id)
      .limit(1);

    if (existing?.length) {
      setError("You have already submitted an application for this vacancy.");
      setSubmitting(false);
      return;
    }

    const { data: createdApplication, error: insertError } = await supabase.from("dc_applications").insert({
      vacancy_id: vacancy.id,
      user_id: session.user.id,
      roblox_username: fixedAnswers.roblox_username.trim(),
      discord_username: fixedAnswers.discord_username.trim(),
      email: fixedAnswers.email.trim(),
      roleplay_name: fixedAnswers.roleplay_name.trim(),
      submitted_answers: customAnswers,
      status: "Received",
    }).select("*").single();

    if (insertError) {
      setError(insertError.message || "Your application could not be submitted.");
    } else {
      setApplication(createdApplication || null);
      setSubmitted(true);
    }
    setSubmitting(false);
  }

  function renderQuestion(question) {
    const idValue = question.id;
    const label = question.label || question.question || "Question";
    const value = customAnswers[idValue] ?? "";
    const setValue = (nextValue) => setCustomAnswers((current) => ({ ...current, [idValue]: nextValue }));

    if (question.type === "long" || question.type === "long_text" || question.type === "textarea") {
      return <textarea className="nhsuk-textarea" rows="5" value={value} onChange={(event) => setValue(event.target.value)} required={question.required} />;
    }
    if (question.type === "yes_no") {
      return (
        <select className="nhsuk-select" value={value} onChange={(event) => setValue(event.target.value)} required={question.required}>
          <option value="">Select an answer</option><option value="Yes">Yes</option><option value="No">No</option>
        </select>
      );
    }
    if (question.type === "select" || question.type === "multiple_choice") {
      return (
        <select className="nhsuk-select" value={value} onChange={(event) => setValue(event.target.value)} required={question.required}>
          <option value="">Select an answer</option>
          {(question.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      );
    }
    return <input type={question.type === "email" ? "email" : "text"} className="nhsuk-input" value={value} placeholder={question.placeholder || ""} onChange={(event) => setValue(event.target.value)} required={question.required} />;
  }

  return (
    <div className="pguh-public-page-shell">
      <PublicHeader session={session} profile={profile} />
      <main id="main-content" tabIndex="-1" className="pguh-public-standard-page">
        <div className="nhsuk-width-container pguh-public-vacancy-detail">
          <Link className="pguh-public-back-link" to="/vacancies"><FaArrowLeft aria-hidden="true" /> Back to vacancies</Link>

          {loading ? <div className="pguh-public-empty"><p>Loading vacancy…</p></div> : error && !vacancy ? (
            <div className="pguh-public-error" role="alert">{error}</div>
          ) : vacancy && (
            <>
              <header className="pguh-public-vacancy-detail__hero">
                <span className="pguh-public-open-badge">Open</span>
                <h1>{vacancy.title}</h1>
                <div className="pguh-public-vacancy-card__meta">
                  {vacancy.department && <span><FaBriefcase aria-hidden="true" /> {vacancy.department}</span>}
                  {vacancy.location && <span><FaMapMarkerAlt aria-hidden="true" /> {vacancy.location}</span>}
                  <span><FaCalendarAlt aria-hidden="true" /> {vacancy.closing_date ? `Closes ${formatDate(vacancy.closing_date)}` : "Open until filled"}</span>
                </div>
              </header>

              <div className="pguh-public-vacancy-detail__grid">
                <article className="pguh-public-content-card">
                  <h2>About this vacancy</h2>
                  <p className="pguh-public-prewrap">{vacancy.description || "No vacancy description has been added."}</p>
                  {vacancy.requirements && <><h2>Requirements</h2><p className="pguh-public-prewrap">{vacancy.requirements}</p></>}
                  {vacancy.application_intro && <><h2>Before you apply</h2><p className="pguh-public-prewrap">{vacancy.application_intro}</p></>}
                </article>

                <aside className="pguh-public-application-card">
                  <h2>Apply online</h2>
                  {submitted ? (
                    <>
                      <div className="pguh-public-success" role="status">
                        <strong>Application submitted</strong>
                        <p>Your application has been sent to the recruitment team.</p>
                        <Link className="pguh-public-inline-link" to="/my-applications">View all of my applications</Link>
                      </div>
                      {application && <ApplicationChat applicationId={application.id} />}
                    </>
                  ) : !session ? (
                    <>
                      <p>You need an RUHT account before you can submit an application.</p>
                      <Link className="nhsuk-button" to="/login" state={{ from: location.pathname }}>Sign in or create an account</Link>
                    </>
                  ) : (
                    <form onSubmit={submitApplication}>
                      {error && <div className="pguh-public-error" role="alert">{error}</div>}
                      <p className="pguh-public-muted">The first four questions are required on every RUHT application.</p>
                      {FIXED_QUESTIONS.map((question) => (
                        <div className="nhsuk-form-group" key={question.key}>
                          <label className="nhsuk-label" htmlFor={`fixed-${question.key}`}>{question.label}</label>
                          <input id={`fixed-${question.key}`} className="nhsuk-input" value={fixedAnswers[question.key]} onChange={(event) => setFixedAnswers((current) => ({ ...current, [question.key]: event.target.value }))} required />
                        </div>
                      ))}

                      {questions.map((question) => (
                        <div className="nhsuk-form-group" key={question.id}>
                          <label className="nhsuk-label" htmlFor={`question-${question.id}`}>
                            {question.label || question.question || "Question"}{question.required ? " *" : ""}
                          </label>
                          {question.help_text && <div className="nhsuk-hint">{question.help_text}</div>}
                          {renderQuestion(question)}
                        </div>
                      ))}

                      <button className="nhsuk-button" type="submit" disabled={submitting}>{submitting ? "Submitting…" : "Submit application"}</button>
                    </form>
                  )}
                </aside>
              </div>
            </>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
